/**
 *
 * FNAnalysisFNStatementsIncomeStatement
 *
 */

import React from "react";
import HelpIconButtonWithTooltipAndTypeformModal from "../../typeform/HelpIconButtonWithTooltipAndTypeformModal";
import TypeformContent from "../../typeform/TypeformContent";
import Teams from '../../../api/Teams';

// Components

/**
 * Price in millions.
 */
const PriceInMillions = ({ price, baseClassName, isPerformanceFeedback = false }) => {
  if (typeof price !== "number") {
    return <div className={baseClassName} style={isPerformanceFeedback ? { color: '#000' } : null}>n/a</div>;
  }

  let newPrice;
  if (price < 0) {
    newPrice = (price / 1000000).toFixed(2) * -1;
    return (
      <div className={`font-red ${baseClassName}`}>{`-$${newPrice}M`}</div>
    );
  } else {
    newPrice = (price / 1000000).toFixed(2);
    return <div className={baseClassName} style={isPerformanceFeedback ? { color: '#000' } : null}>{`$${newPrice}M`}</div>;
  }
};

/**
 * Table row contents
 */

const TableHead = ({ current_year }) => (
  <thead>
    <tr>
      <th className="table-title text-center">
        <span />
      </th>
      <th className="table-title title-gray text-center">
        YEAR {current_year}
      </th>
    </tr>
  </thead>
);

/**
 * Table row contents
 */

const TableRowContents = ({ incomeStatement, name, isPerformanceFeedback, preColumn, rowSpan = 1 }) => (
  <tr className="text-center">
    {preColumn && (
      <th className="description bold-font" rowSpan={rowSpan} style={isPerformanceFeedback ? { border: 'none', color: Teams.currentTeamColor(), textAlign: 'left', verticalAlign: 'middle' } : null}>{preColumn}</th>
    )}
    <th className="description" style={isPerformanceFeedback ? { border: 'none', color: '#000', textAlign: 'right' } : null}>{name ? name : incomeStatement.name}</th>
    <td style={isPerformanceFeedback ? { border: 'none', textAlign: 'left' } : null}>
      <PriceInMillions
        baseClassName="text-center"
        price={incomeStatement.data.value}
        isPerformanceFeedback={isPerformanceFeedback}
      />
    </td>
  </tr>
);

/**
 * Table row contents with gray background color.
 */

const TableRowContentsFooter = ({ incomeStatement, isPerformanceFeedback }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description" style={isPerformanceFeedback ? {
      border: 'none',
      color: '#000',
      textAlign: 'right',
      backgroundColor: Teams.currentTeamColor(),
      borderTopLeftRadius: '100px',
      borderBottomLeftRadius: '100px',
    } : null}>
      <strong>
        {incomeStatement.name}
        {incomeStatement.name === "Gross Profit" && !isPerformanceFeedback && (
          <HelpIconButtonWithTooltipAndTypeformModal
            iconSize="sm"
            {...TypeformContent.GROSS_PROFITS}
          />
        )}
      </strong>
    </th>
    <td style={isPerformanceFeedback ? { border: 'none', textAlign: 'left', backgroundColor: Teams.currentTeamColor() } : null}>
      <PriceInMillions
        baseClassName="text-center"
        price={incomeStatement.data.value}
        isPerformanceFeedback={isPerformanceFeedback}
      />
    </td>
  </tr>
);

/**
 * Table row total content
 */

const TotalComponent = ({ incomeStatement, title, isPerformanceFeedback }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description" style={isPerformanceFeedback ? {
      border: 'none',
      color: '#000',
      textAlign: 'right',
      backgroundColor: Teams.currentTeamColor(),
      borderTopLeftRadius: '100px',
      borderBottomLeftRadius: '100px',
    } : null}>
      <strong>
        {title ? title : incomeStatement.name}
        {title === "Net Profits" && !isPerformanceFeedback && (
          <HelpIconButtonWithTooltipAndTypeformModal
            iconSize="sm"
            {...TypeformContent.NET_PROFITS}
          />
        )}
      </strong>
    </th>
    <td style={isPerformanceFeedback ? { border: 'none', textAlign: 'left', backgroundColor: Teams.currentTeamColor() } : null}>
      <PriceInMillions
        baseClassName="text-center"
        price={incomeStatement.data.value}
        isPerformanceFeedback={isPerformanceFeedback}
      />
    </td>
  </tr>
);

/**
 * Table row operating profit
 */

const OperatingProfitComponent = ({ totalOperatingExpenses, grossProfits, isPerformanceFeedback }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description" style={isPerformanceFeedback ? {
      border: 'none',
      color: '#000',
      textAlign: 'right',
      backgroundColor: Teams.currentTeamColor(),
      borderTopLeftRadius: '100px',
      borderBottomLeftRadius: '100px',
    } : null}>
      <strong>{isPerformanceFeedback ? 'Operating Profit' : 'Operation Profits'}</strong>
    </th>
    <td style={isPerformanceFeedback ? { border: 'none', textAlign: 'left', backgroundColor: Teams.currentTeamColor() } : null}>
      <PriceInMillions
        baseClassName="text-center"
        price={totalOperatingExpenses.data.value + grossProfits.data.value}
        isPerformanceFeedback={isPerformanceFeedback}
      />
    </td>
  </tr>
);

/**
 * Table row title.
 */

const TotalTitleHeader = ({ title }) => (
  <tr className="text-center">
    <th className="sub-title title-sky-blue">{title}</th>
    <td>
      <div className="row flex">
        <div className="col-xs-6" />
        <div className="col-xs-6" />
        <div className="col-xs-6" />
      </div>
    </td>
  </tr>
);

/**
 *
 * FNAnalysisFNStatementsIncomeStatement
 *
 */
function IncomeStatementWidget({
  incomeStatement,
  current_year,
  className = "team-reports-with-scroll",
  isPerformanceFeedback = false,
}) {
  const {
    costOfSales,
    revenues,
    grossProfits,
    RnDCosts,
    holdingCosts,
    promotionCosts,
    sellingCosts,
    gnACosts,
    incomingBeforeTaxes,
    taxes,
    totalNonOperatingCost,
    interestExpense,
    totalOperatingExpenses,
    gainOrLossoFSales,
    otherNonOperatingCosts,
    netIncome
  } = incomeStatement;

  return (
    <div className={`panel-dashboard-white-full ${className}`}>
      <h5 className="title-sky-blue">
        Income Statement
        <HelpIconButtonWithTooltipAndTypeformModal
          iconSize="sm"
          {...TypeformContent.INCOME_STATEMENT}
        />
      </h5>
      {isPerformanceFeedback && (
        <h5 className="title-sky-blue visible-title">Income Statement</h5>
      )}
      <div className="table-responsive fn-results-table" style={isPerformanceFeedback ? { paddingBottom: '20px' } : null}>
        <table className="table border-analysis mb-20">
          {!isPerformanceFeedback && <TableHead current_year={current_year} />}
          {Object.keys(costOfSales.data).length > 0 ||
          Object.keys(revenues.data).length > 0 ||
          Object.keys(grossProfits.data).length > 0 ? (
            <tbody>
              <TableRowContents incomeStatement={revenues} isPerformanceFeedback={isPerformanceFeedback} rowSpan={3} preColumn={isPerformanceFeedback ? 'Gross Profits' : null} />
              <TableRowContents incomeStatement={costOfSales} isPerformanceFeedback={isPerformanceFeedback} />
              <TableRowContentsFooter incomeStatement={grossProfits} isPerformanceFeedback={isPerformanceFeedback} />
              {!isPerformanceFeedback && <TotalTitleHeader title="Operating Expenses" />}
              <TableRowContents incomeStatement={RnDCosts} isPerformanceFeedback={isPerformanceFeedback} rowSpan={6} preColumn={isPerformanceFeedback ? 'Operating Profit' : null} />
              <TableRowContents incomeStatement={holdingCosts} isPerformanceFeedback={isPerformanceFeedback} />
              <TableRowContents incomeStatement={promotionCosts} isPerformanceFeedback={isPerformanceFeedback} />
              <TableRowContents incomeStatement={sellingCosts} isPerformanceFeedback={isPerformanceFeedback} />
              <TableRowContents
                incomeStatement={gnACosts}
                name="Other G&A Costs"
                isPerformanceFeedback={isPerformanceFeedback}
              />
              {!isPerformanceFeedback && (
                <TableRowContentsFooter
                  incomeStatement={totalOperatingExpenses}
                />
              )}
              <OperatingProfitComponent
                totalOperatingExpenses={totalOperatingExpenses}
                grossProfits={grossProfits}
                isPerformanceFeedback={isPerformanceFeedback}
              />
              {!isPerformanceFeedback && <TotalTitleHeader title="Non-Operating Costs" />}
              <TableRowContents incomeStatement={interestExpense} isPerformanceFeedback={isPerformanceFeedback} rowSpan={2} preColumn={isPerformanceFeedback ? 'Net Profits' : null} />
              {!isPerformanceFeedback && <TableRowContents incomeStatement={gainOrLossoFSales} />}
              {!isPerformanceFeedback && <TableRowContents incomeStatement={otherNonOperatingCosts} />}
              {!isPerformanceFeedback && <TotalComponent incomeStatement={totalNonOperatingCost} />}
              {!isPerformanceFeedback && <TotalTitleHeader title="Profits" />}
              {!isPerformanceFeedback && <TableRowContents incomeStatement={incomingBeforeTaxes} />}
              {!isPerformanceFeedback && <TableRowContents incomeStatement={taxes} />}
              <TotalComponent incomeStatement={netIncome} title="Net Profits" isPerformanceFeedback={isPerformanceFeedback} />
            </tbody>
          ) : (
            <tbody className="empty-table" />
          )}
        </table>
      </div>
    </div>
  );
}

export default IncomeStatementWidget;



// WEBPACK FOOTER //
// ./src/components/finance/widgets/income-statement.js